/**
 * Wallet (saved cards) — user (`payments/wallet`) and org
 * (`organizations/:id/wallet`) variants through the same set of functions,
 * parametrized by owner (`WalletOwnerArgs`).
 *
 * BE returns RAW responses (no `{ data }` wrapper) — see payments.types.ts.
 */
import type {
  SetDefaultWalletInstrumentArgs,
  ApiSetDefaultInstrumentResult,
  RemoveWalletInstrumentArgs,
  SetupWalletInstrumentArgs,
  ResolveWalletSetupArgs,
  ApiResolveWalletSetup,
  ApiPaymentInstrument,
  ApiRedirectResult,
  WalletOwnerArgs,
} from './types/payments.types';

import { destroy, post, get } from './axios';

function walletBasePath(args: WalletOwnerArgs) {
  return args.ownerType === 'organization'
    ? `/v2/organizations/${args.organizationId}/wallet`
    : '/v2/payments/wallet';
}

export const fetchWalletInstruments = async (args: WalletOwnerArgs) => {
  const response = await get<ApiPaymentInstrument[]>(walletBasePath(args));
  return response ?? [];
};

export const setupWalletInstrument = async (
  args: SetupWalletInstrumentArgs,
) => {
  const response = await post(`${walletBasePath(args)}/setup`, {
    cancelUrl: args.cancelUrl,
    provider: args.provider,
    successUrl: args.successUrl,
  });
  // `post` helper types the `{ data }` wrapper, but BE returns a raw body here — re-typing.
  return (response ?? null) as ApiRedirectResult | null;
};

// self-heal on return from Stripe setup (R-AUD-53): BE fetches the session
// outcome without waiting for the webhook — the card is visible right away
export const resolveWalletSetup = async (args: ResolveWalletSetupArgs) => {
  const response = await post(`${walletBasePath(args)}/setup/resolve`, {
    sessionId: args.sessionId,
  });
  return (response ?? null) as ApiResolveWalletSetup | null;
};

export const removeWalletInstrument = async (
  args: RemoveWalletInstrumentArgs,
) => {
  return destroy(`${walletBasePath(args)}/${args.instrumentId}`);
};

// R-AUD-74: "set as default" — BE atomically switches the default; raw response
export const setDefaultWalletInstrument = async (
  args: SetDefaultWalletInstrumentArgs,
) => {
  const response = await post(
    `${walletBasePath(args)}/${args.instrumentId}/default`,
    {},
  );
  return (response ?? null) as ApiSetDefaultInstrumentResult | null;
};
